import React, { useEffect, useRef, useState } from "react";
import styles from "./simulatedTransaction.module.scss";
import {
  Button,
  message as messageAnt,
  DatePicker,
  DatePickerProps,
  TimePickerProps,
  Select,
  SelectProps,
  TimePicker,
  Input,
} from "antd";
import { Candle, ITrade, TimeFrames } from "../../shared/types";
import { useSimulatedTransaction } from "../../hooks/simulatedTransaction.hook";
import { CountryExchange } from "../../shared/countryExchange";
import { useTickerList } from "../../hooks/tickerList.hook";
import Chart from "./chart/Chart";
import { translate } from "./translation";
import {
  calcProfit,
  chooseUnitFromDays,
  formatHook,
  formatWhen,
  getDaysBetween,
  getRandom,
  getRandomTransformCanvas,
} from "./functions";
import { usePositions } from "../../hooks/positions.hook";
import { delay, frameGate, stripHTML } from "../../shared/lib/lib";
import { accentColorList, backgroundList } from "./variables";

enum Languages {
  "en" = "en",
  "ru" = "ru",
  "es" = "es",
  "pt" = "pt",
  "de" = "de",
  "fr" = "fr",
}

const buildTickerOptions = (tickers: string[], searchValue: string): SelectProps["options"] => {
  const search = searchValue.trim().toUpperCase();

  return tickers
    .filter(ticker => !search || ticker.toUpperCase().startsWith(search))
    .sort((tickerA, tickerB) => {
      const normalizedTickerA = tickerA.toUpperCase();
      const normalizedTickerB = tickerB.toUpperCase();

      if (normalizedTickerA === search && normalizedTickerB !== search) {
        return -1;
      }

      if (normalizedTickerB === search && normalizedTickerA !== search) {
        return 1;
      }

      return normalizedTickerA.localeCompare(normalizedTickerB);
    })
    .map(ticker => ({ value: ticker, label: ticker }));
};
const SimulatedTransaction = () => {
  const { loading, getChart, error, clearError } = useSimulatedTransaction();
  const {
    loading: loadingTickerList,
    getTickers,
    error: errorTickerList,
    clearError: clearErrorTickerList,
  } = useTickerList();
  const {
    loading: loadingPositions,
    sendTradeForVideo,
    error: errorPositions,
    clearError: clearErrorPositions,
  } = usePositions();

  const [simulationIsInProcess, setSimulationIsInProcess] = useState<boolean>(false);
  const [greetingEnded, setGreetingEnded] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<Candle[]>(); //график //[["open", "close", "high", "low", "value", "volume", "begin", "end"], ]
  const [exchangeName, setExchangeName] = useState<string>(""); //наименование биржи на которой торгуется бумага
  const [companyName, setCompanyName] = useState<string>(""); //полное название компании
  const [regularMarketPrice, setRegularMarketPrice] = useState<string>(""); //текущая цена, для определения количества порядков после запятой, т.к. дифференциала в api yahoo нет, а все другие цены много лишних знаков после запятой
  const [optionsSelectTimeframe, setOptionsSelectTimeframe] = useState<SelectProps["options"]>(); //массив таймфреймов, доступных для выбора
  const [timeframe, setTimeframe] = useState<TimeFrames>();
  const [optionsSelectTicker, setOptionsSelectTicker] = useState<SelectProps["options"]>(); //массив тикеров, доступных для выбора
  const [ticker, setTicker] = useState<string>("");
  const [tickerSearchValue, setTickerSearchValue] = useState<string>("");
  const [countryExchange, setCountryExchange] = useState<CountryExchange | undefined>(CountryExchange.US);
  const [tickerList, setTickerList] = useState<string[]>([]);
  const [dateOpenOrder, setDateOpenOrder] = useState<string>(); //дата открытия позиции
  const [timeOpenOrder, setTimeOpenOrder] = useState<string>(); //время открытия позиции
  const [dateTimeOpenOrder, setDateTimeOpenOrder] = useState<string>(); //дата и время открытия позиции
  const [priceOpenOrder, setPriceOpenOrder] = useState<string>(); //цена открытия позиции
  const [dateAdd1Order, setDateAdd1Order] = useState<string>();
  const [timeAdd1Order, setTimeAdd1Order] = useState<string>();
  const [dateTimeAdd1Order, setDateTimeAdd1Order] = useState<string>();
  const [priceAdd1Order, setPriceAdd1Order] = useState<string>();
  const [dateAdd2Order, setDateAdd2Order] = useState<string>();
  const [timeAdd2Order, setTimeAdd2Order] = useState<string>();
  const [dateTimeAdd2Order, setDateTimeAdd2Order] = useState<string>();
  const [priceAdd2Order, setPriceAdd2Order] = useState<string>();
  const [dateCloseOrder, setDateCloseOrder] = useState<string>();
  const [timeCloseOrder, setTimeCloseOrder] = useState<string>();
  const [dateTimeCloseOrder, setDateTimeCloseOrder] = useState<string>();
  const [priceCloseOrder, setPriceCloseOrder] = useState<string>();
  const [optionsSelectLanguage, setOptionsSelectLanguage] = useState<SelectProps["options"]>(); //массив языков для отображения интерфейса
  const [language, setLanguage] = useState<Languages>(Languages.en);
  const [openChartBtnDisabled, setOpenChartBtnDisabled] = useState(true); //блокировка кнопки если не все необходимые данные выбраны
  const [simulationBtnDisabled, setSimulationBtnDisabled] = useState(true); //блокировка кнопки если не все необходимые данные выбраны
  const [chartIsOpen, setChartIsOpen] = useState(false); //состояние открытия графика
  const [priceDifferential, setPriceDifferential] = useState<number>(); //количество знаков после запятой у цены для этого тикера
  const [hookTitle, setHookTitle] = useState<Record<Languages, string>>(); //фраза заголовок, на всех языках
  const [hookSecondTitle, setHookSecondTitle] = useState<Record<Languages, string>>(); //фраза второй заголовок, на всех языках
  const [secondTitleIsActive, setHookSecondTitleIsActive] = useState<Boolean>(false); //активен ли уже второй заголовок
  const [disclaimer, setDisclaimer] = useState<Record<Languages, string>>(); //фраза disclaimer, на всех языках
  const [accentColor, setAccentColor] = useState<string>(""); //цвет акцентов в заголовке
  const [transformCanvas, setTransformCanvas] = useState<{ widthRatio: number; heightRatio: number }>({
    widthRatio: 1,
    heightRatio: 1,
  }); //трансформации канвас чтобы убрать повторяемость

  //ссылки на анимируемые элементы
  const movieRefTitle = useRef<HTMLDivElement>(null);
  const movieRefChart = useRef<HTMLDivElement>(null);
  const movieRefBg = useRef<HTMLDivElement>(null);

  //определение количества знаков после запятой, до которой нужно округлять цены
  useEffect(() => {
    if (regularMarketPrice) {
      const split = regularMarketPrice.toString().split(".");
      const num = split[1].length;
      setPriceDifferential(num);
    }
  }, [regularMarketPrice]);

  //управление активностью кнопки открытия графика
  useEffect(() => {
    if (quotes && dateTimeOpenOrder && dateTimeCloseOrder && priceOpenOrder && priceCloseOrder) {
      setOpenChartBtnDisabled(false);
      setChartIsOpen(false);
    } else {
      setOpenChartBtnDisabled(true);
      setChartIsOpen(false);
    }
  }, [quotes, dateTimeOpenOrder, dateTimeCloseOrder, priceOpenOrder, priceCloseOrder]);

  //управление активностью кнопки запуска симуляции
  useEffect(() => {
    if (chartIsOpen) {
      setSimulationBtnDisabled(false);
    }
  }, [chartIsOpen]);

  //открытие графика
  const openChartHandler = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!chartIsOpen) {
      setChartIsOpen(true);
    }
  };

  //кнопка запуска симуляции
  const simulationBtnHandler = async () => {
    if (!simulationIsInProcess) {
      setSimulationIsInProcess(true);
    }
  };

  //кнопка записи симуляции на сервере
  const simulationOnServerBtnHandler = async () => {
    if (
      ticker &&
      timeframe &&
      dateOpenOrder &&
      timeOpenOrder &&
      priceOpenOrder &&
      dateCloseOrder &&
      timeCloseOrder &&
      priceCloseOrder &&
      hookTitle
    ) {
      if (!simulationIsInProcess) {
        const trade: ITrade = {
          ticker: ticker,
          timeframe: timeframe,
          dateOpen: dateOpenOrder,
          timeOpen: timeOpenOrder,
          priceOpen: priceOpenOrder,
          dateAdd1: dateAdd1Order,
          timeAdd1: timeAdd1Order,
          priceAdd1: priceAdd1Order,
          dateAdd2: dateAdd2Order,
          timeAdd2: timeAdd2Order,
          priceAdd2: priceAdd2Order,
          dateClose: dateCloseOrder,
          timeClose: timeCloseOrder,
          priceClose: priceCloseOrder,
        };

        await sendTradeForVideo(trade);
      }
    }
  };

  //функция получения списка тикеров по выбранной стране
  const getTickerList = async (country: CountryExchange) => {
    try {
      const dataTickers = await getTickers(country);
      messageAnt.info(dataTickers.message);
      setTickerList(dataTickers.info);
    } catch (e) {
      if (e instanceof Error) {
        messageAnt.error(e.message);
      }
      setCountryExchange(undefined);
      clearErrorTickerList();
    }
  };

  //получение списка тикеров с сайта ru.investing.com в зависимости от выбранной страны
  useEffect(() => {
    if (countryExchange) {
      const func = async () => {
        await getTickerList(countryExchange);
      };
      func();
    }
  }, [countryExchange]);

  //функция получения графика с сервера
  const getQuotes = async (ticker: string, timeframe: string) => {
    try {
      const data = await getChart(ticker, timeframe);
      messageAnt.info(data.message);
      return {
        exchangeName: data.info.exchangeName,
        companyName: data.info.companyName,
        regularMarketPrice: data.info.regularMarketPrice,
        chart: data.info.chart,
      };
    } catch (e) {
      messageAnt.info(error);
      clearError();
    }
  };

  //изначальное получение графика
  useEffect(() => {
    if (ticker && timeframe) {
      const get = async () => {
        const info = await getQuotes(ticker, timeframe);
        if (info && info.chart.length !== 0) {
          setQuotes(info.chart);
          setExchangeName(info.exchangeName);
          setCompanyName(info.companyName);
          setRegularMarketPrice(info.regularMarketPrice);
        }
      };
      get();
    }
  }, [ticker, timeframe]);

  //формирование списков для селекторов выбора
  useEffect(() => {
    setOptionsSelectTicker(buildTickerOptions(tickerList, tickerSearchValue));
    const optionsTimeframe: SelectProps["options"] = [];
    Object.keys(TimeFrames).forEach(timeFrame => {
      optionsTimeframe.push({ value: timeFrame, label: timeFrame });
    });
    setOptionsSelectTimeframe(optionsTimeframe);
    const optionsLanguage: SelectProps["options"] = [];
    Object.keys(Languages).forEach(language => {
      optionsLanguage.push({
        value: language,
        label: language,
      });
    });
    setOptionsSelectLanguage(optionsLanguage);
  }, [tickerList, tickerSearchValue]);

  //выбор таймфрейма для получения исторических данных
  const onChangeTimeframe = (value: string) => {
    setTimeframe(value as TimeFrames);
  };
  //выбор инструмента для получения исторических данных
  const onChangeTicker = (value: string) => {
    setTicker(value);
    setTickerSearchValue("");
  };
  //выбор языка отображения сделки
  const onChangeLanguage = (value: string) => {
    setLanguage(value as Languages);
    setSimulationIsInProcess(false);
    setGreetingEnded(false);
    setSimulationBtnDisabled(false);
    setHookSecondTitleIsActive(false);
  };

  //выбор даты открытия позиции
  const dateOpenOrderHandler: DatePickerProps["onChange"] = (date, dateString) => {
    setDateOpenOrder(dateString);
  };
  //выбор времени открытия позиции
  const timeOpenOrderHandler: TimePickerProps["onChange"] = (time, timeString) => {
    setTimeOpenOrder(timeString);
  };
  //выбор цены открытия позиции
  const priceOpenOrderHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceOpenOrder(value);
  };

  //выбор даты добавления1 позиции
  const dateAdd1OrderHandler: DatePickerProps["onChange"] = (date, dateString) => {
    setDateAdd1Order(dateString);
  };
  //выбор времени добавления1 позиции
  const timeAdd1OrderHandler: TimePickerProps["onChange"] = (time, timeString) => {
    setTimeAdd1Order(timeString);
  };
  //выбор цены добавления1 позиции
  const priceAdd1OrderHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceAdd1Order(value);
  };

  //выбор даты добавления2 позиции
  const dateAdd2OrderHandler: DatePickerProps["onChange"] = (date, dateString) => {
    setDateAdd2Order(dateString);
  };
  //выбор времени добавления2 позиции
  const timeAdd2OrderHandler: TimePickerProps["onChange"] = (time, timeString) => {
    setTimeAdd2Order(timeString);
  };
  //выбор цены добавления2 позиции
  const priceAdd2OrderHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceAdd2Order(value);
  };

  //выбор даты закрытия позиции
  const dateCloseOrderHandler: DatePickerProps["onChange"] = (date, dateString) => {
    setDateCloseOrder(dateString);
  };
  //выбор времени закрытия позиции
  const timeCloseOrderHandler: TimePickerProps["onChange"] = (time, timeString) => {
    setTimeCloseOrder(timeString);
  };
  //выбор цены закрытия позиции
  const priceCloseOrderHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceCloseOrder(value);
  };

  //склейвание даты и времени позиции в одну строку
  useEffect(() => {
    if (dateOpenOrder && timeOpenOrder) {
      setDateTimeOpenOrder(dateOpenOrder + " " + timeOpenOrder + ":00");
    }
    if (dateAdd1Order && timeAdd1Order) {
      setDateTimeAdd1Order(dateAdd1Order + " " + timeAdd1Order + ":00");
    }
    if (dateAdd2Order && timeAdd2Order) {
      setDateTimeAdd2Order(dateAdd2Order + " " + timeAdd2Order + ":00");
    }
    if (dateCloseOrder && timeCloseOrder) {
      setDateTimeCloseOrder(dateCloseOrder + " " + timeCloseOrder + ":00");
    }
  }, [
    dateOpenOrder,
    timeOpenOrder,
    dateAdd1Order,
    timeAdd1Order,
    dateAdd2Order,
    timeAdd2Order,
    dateCloseOrder,
    timeCloseOrder,
  ]);

  //рандомная генерация объекта с заголовками, дисклеймера, фона и трансформации графика
  useEffect(() => {
    if (ticker && timeframe && language && chartIsOpen && dateOpenOrder && dateCloseOrder) {
      //рандомно выбираю заголовок из списка для видео
      //выбор шаблона фразы, т.е ключа (inviteHook8) по которой получим фразу
      const hookKey = "inviteHook" + getRandom(100); //у нас сейчас именно 100 хуков для ротации
      //количество дней в сделке
      const numDays = getDaysBetween(dateOpenOrder, dateCloseOrder);
      //выбор человеко понятной единици времени нахождения в сделке по кличеству дней
      const duration = chooseUnitFromDays(numDays);
      //конкретная форма продолжительности в сделке, готовая ко вставке в заголовок
      let when: Record<Languages, string> = {
        en: "",
        ru: "",
        es: "",
        pt: "",
        de: "",
        fr: "",
      };
      Object.keys(Languages).forEach(lang => {
        const whenLang = formatWhen(lang, duration.n, duration.unit);
        when[lang as keyof typeof Languages] = whenLang;
      });
      //набор готовых заголовков для всех языков
      const hook: Record<Languages, string> = {
        en: "",
        ru: "",
        es: "",
        pt: "",
        de: "",
        fr: "",
      };
      Object.keys(Languages).forEach(lang => {
        hook[lang as keyof typeof Languages] = formatHook(
          translate(lang, hookKey),
          companyName,
          ticker,
          when[lang as keyof typeof Languages],
          lang
        );
      });
      setHookTitle(hook);

      //выбор второго заголовка
      const secondHookKey = "inviteHookSecondTitle" + getRandom(100);
      // //набор готовых заголовков для всех языков
      const secondHook: Record<Languages, string> = {
        en: "",
        ru: "",
        es: "",
        pt: "",
        de: "",
        fr: "",
      };
      Object.keys(Languages).forEach(lang => {
        secondHook[lang as keyof typeof Languages] = translate(lang, secondHookKey);
      });
      setHookSecondTitle(secondHook);

      //сохранение полученных заголовков глобально, чтобы puppeteer мог их получить, это для генерации текстового контента для текстовых соц сетей
      if (
        "__globalTitleList" in window &&
        typeof window.__globalTitleList === "string" &&
        "__globalSecondTitleList" in window &&
        typeof window.__globalSecondTitleList === "string"
      ) {
        const titles: Record<string, string> = {};
        const secondTitles: Record<string, string> = {};
        Object.keys(Languages).forEach(lang => {
          const title = stripHTML(hook[lang as keyof typeof Languages]);
          titles[lang] = title;
          const secondTitle = secondHook[lang as keyof typeof Languages];
          secondTitles[lang] = secondTitle;
        });
        window.__globalTitleList = JSON.stringify(titles);
        window.__globalSecondTitleList = JSON.stringify(secondTitles);
      }

      //рандомно выбираю дисклеймер из списка для видео
      const disclaimerKey = "disclaimer" + getRandom(12); //у нас сейчас именно 12 дисклеймеров для ротации
      //набор готовых заголовков для всех языков
      const dl: Record<Languages, string> = {
        en: "",
        ru: "",
        es: "",
        pt: "",
        de: "",
        fr: "",
      };
      Object.keys(Languages).forEach(lang => {
        dl[lang as keyof typeof Languages] = translate(lang, disclaimerKey);
      });
      setDisclaimer(dl);

      //рандомно выбираю фон из списка для видео
      const colorIndex = getRandom(50) - 1; //у нас сейчас именно 50 цветов фона для ротации
      const gotColor = backgroundList[colorIndex];
      if (movieRefBg.current) {
        movieRefBg.current.style.background = gotColor;
      }

      //рандомно выбираю трансформация для графика
      const transformCanvas = getRandomTransformCanvas();
      setTransformCanvas(transformCanvas);
    }
  }, [ticker, timeframe, language, chartIsOpen, dateOpenOrder, dateCloseOrder, movieRefBg]);

  //рандомно выбираю цвет для акцентного текста из списка
  useEffect(() => {
    if (movieRefTitle.current && hookTitle) {
      const colorAccentIndex = getRandom(10) - 1; //у нас сейчас именно 10 акцентных цветов для ротации
      const gotAccentColor = accentColorList[colorAccentIndex];
      const tagP = movieRefTitle.current.querySelector("p");
      if (tagP) {
        const tickerSpan = tagP.querySelector<HTMLSpanElement>('[data-accent="ticker"]');
        if (tickerSpan) {
          tickerSpan.style.color = gotAccentColor;
        }
      }
      setAccentColor(gotAccentColor);
    }
  }, [movieRefTitle, hookTitle, language]);

  //js анимация отображения контента
  useEffect(() => {
    const func = async () => {
      //рандомизация пауз для не повторяемости
      const randomFirstPause = getRandom(1000);
      const randomSecondPause = getRandom(1000);

      const startShowingTimingTitle = 300 + randomFirstPause;
      const endShowingTimingTitle = 600 + randomFirstPause;
      const startShowingTimingChart = 1100 + randomFirstPause + randomSecondPause;
      const endShowingTimingChart = 1600 + randomFirstPause + randomSecondPause;

      const interval = 33; //fps 30

      const durationShowingTitle = endShowingTimingTitle - startShowingTimingTitle;
      const countIterationShowingTitle = Math.floor(durationShowingTitle / interval);
      const opacityIncrementShowingTitle = 1.0 / countIterationShowingTitle;
      const durationShowingChart = endShowingTimingChart - startShowingTimingChart;
      const countIterationShowingChart = Math.floor(durationShowingChart / interval);
      const opacityIncrementShowingChart = 1.0 / countIterationShowingChart;

      let opacityTitle = 0;
      let opacityChart = 0;

      if (simulationIsInProcess && movieRefChart.current && movieRefTitle.current) {
        movieRefTitle.current.style.opacity = "0";
        movieRefChart.current.style.opacity = "0";
        await delay(2000);
        for (let i = 0; i < endShowingTimingChart / interval; i++) {
          //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
          await frameGate();
          await delay(200);
          const timing = i * interval;
          if (timing < startShowingTimingTitle) {
            //ничего не меняем
          } else if (timing < endShowingTimingTitle) {
            if (timing + interval < endShowingTimingTitle) {
              movieRefTitle.current.style.opacity = opacityTitle.toString();
              opacityTitle += opacityIncrementShowingTitle;
            } else {
              movieRefTitle.current.style.opacity = "1.0";
              opacityTitle = 1;
            }
          } else if (timing < startShowingTimingChart) {
            //ничего не меняем
          } else if (timing < endShowingTimingChart) {
            if (timing + interval < endShowingTimingChart) {
              movieRefChart.current.style.opacity = opacityChart.toString();
              opacityChart += opacityIncrementShowingChart;
            } else {
              movieRefChart.current.style.opacity = "1.0";
              opacityChart = 1;
            }
          }
        }
        setGreetingEnded(true);
      }
    };
    func();
  }, [movieRefChart, movieRefTitle, simulationIsInProcess]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <p className={styles.title}>Симулятор сделок</p>
        </div>
        <div className={styles.table}>
          <h5>Настройки получения данных</h5>
          <div className={[styles.settingsChart, styles.blockWrap].join(" ")}>
            <p className={styles.title}>Настройки получения графика</p>
            <Select
              showSearch
              allowClear
              searchValue={tickerSearchValue}
              filterOption={false}
              onSearch={setTickerSearchValue}
              placeholder="Тикер"
              onChange={onChangeTicker}
              options={optionsSelectTicker}
              style={{ width: 152 }}
              size={"small"}
            />
            <Select
              showSearch
              allowClear
              placeholder="Таймфрейм"
              onChange={onChangeTimeframe}
              options={optionsSelectTimeframe}
              style={{ width: 152 }}
              size={"small"}
            />
            <Select
              showSearch
              allowClear
              placeholder="Язык"
              onChange={onChangeLanguage}
              options={optionsSelectLanguage}
              style={{ width: 120 }}
              size={"small"}
              defaultValue={Languages.en}
            />
          </div>
          <div className={[styles.settingsOrders, styles.blockWrap].join(" ")}>
            <p className={styles.title}>Настройки произведенных сделок</p>
            <div className={styles.line}>
              <p className={styles.lineTitle}>Открытие</p>
              <DatePicker
                placeholder="Дата"
                onChange={dateOpenOrderHandler}
                style={{ width: 120, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <TimePicker
                placeholder="Время"
                showNow={false}
                format={"HH:mm"}
                onChange={timeOpenOrderHandler}
                style={{ width: 80, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <Input
                placeholder="Цена"
                onChange={priceOpenOrderHandler}
                style={{ width: 130, height: 24 }}
                size={"small"}
              />
            </div>
            <div className={styles.line}>
              <p className={styles.lineTitle}>Добавление 1</p>
              <DatePicker
                placeholder="Дата"
                onChange={dateAdd1OrderHandler}
                style={{ width: 120, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <TimePicker
                placeholder="Время"
                showNow={false}
                format={"HH:mm"}
                onChange={timeAdd1OrderHandler}
                style={{ width: 80, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <Input
                placeholder="Цена"
                onChange={priceAdd1OrderHandler}
                style={{ width: 130, height: 24 }}
                size={"small"}
              />
            </div>
            <div className={styles.line}>
              <p className={styles.lineTitle}>Добавление 2</p>
              <DatePicker
                placeholder="Дата"
                onChange={dateAdd2OrderHandler}
                style={{ width: 120, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <TimePicker
                placeholder="Время"
                showNow={false}
                format={"HH:mm"}
                onChange={timeAdd2OrderHandler}
                style={{ width: 80, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <Input
                placeholder="Цена"
                onChange={priceAdd2OrderHandler}
                style={{ width: 130, height: 24 }}
                size={"small"}
              />
            </div>
            <div className={styles.line}>
              <p className={styles.lineTitle}>Закрытие</p>
              <DatePicker
                placeholder="Дата"
                onChange={dateCloseOrderHandler}
                style={{ width: 120, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <TimePicker
                placeholder="Время"
                showNow={false}
                format={"HH:mm"}
                onChange={timeCloseOrderHandler}
                style={{ width: 80, height: 24 }}
                size={"small"}
                popupStyle={{ top: "140px", left: "23px" }}
              />
              <Input
                placeholder="Цена"
                onChange={priceCloseOrderHandler}
                style={{ width: 130, height: 24 }}
                size={"small"}
              />
            </div>
            <Button
              title={"Открыть график"}
              type={"primary"}
              onClick={openChartHandler}
              style={{ width: 334 }}
              size={"small"}
              disabled={openChartBtnDisabled}>
              Открыть график
            </Button>
            <Button
              title={"Запустить симуляцию"}
              type={"primary"}
              onClick={simulationBtnHandler}
              style={{ width: 334 }}
              size={"small"}
              disabled={simulationBtnDisabled}>
              Запустить симуляцию
            </Button>
            <Button
              title={"Записать симуляцию на сервере"}
              type={"primary"}
              onClick={simulationOnServerBtnHandler}
              style={{ width: 334 }}
              size={"small"}
              disabled={simulationBtnDisabled}>
              Записать симуляцию на сервере
            </Button>
          </div>
          <div id={"movieContainer"} className={[styles.chart, styles.blockWrap, styles.movieContainer].join(" ")}>
            <div className={styles.movieContent} ref={movieRefBg}>
              <div
                className={[styles.title, secondTitleIsActive ? styles.secondTitleIsActive : ""].join(" ")}
                ref={movieRefTitle}>
                {hookTitle && hookSecondTitle && (
                  <p className={styles.firstTitle} dangerouslySetInnerHTML={{ __html: hookTitle[language] }} />
                )}
                {hookTitle && hookSecondTitle && (
                  <p className={styles.secondTitle} dangerouslySetInnerHTML={{ __html: hookSecondTitle[language] }} />
                )}
              </div>
              <div className={styles.chartWrap} ref={movieRefChart}>
                {chartIsOpen &&
                  quotes &&
                  timeframe &&
                  priceDifferential &&
                  dateTimeOpenOrder &&
                  dateTimeCloseOrder &&
                  priceOpenOrder &&
                  priceCloseOrder && (
                    <Chart
                      simulationIsInProcess={simulationIsInProcess}
                      greetingEnded={greetingEnded}
                      chart={quotes}
                      timeframe={timeframe}
                      dateTimeOpenOrder={dateTimeOpenOrder}
                      dateTimeAdd1Order={dateTimeAdd1Order}
                      dateTimeAdd2Order={dateTimeAdd2Order}
                      dateTimeCloseOrder={dateTimeCloseOrder}
                      priceOpenOrder={priceOpenOrder}
                      priceAdd1Order={priceAdd1Order}
                      priceAdd2Order={priceAdd2Order}
                      priceCloseOrder={priceCloseOrder}
                      language={language}
                      ticker={ticker}
                      exchangeName={exchangeName}
                      companyName={companyName}
                      accentColor={accentColor}
                      transformCanvas={transformCanvas}
                      movieRefTitle={movieRefTitle}
                      setHookSecondTitleIsActive={setHookSecondTitleIsActive}
                    />
                  )}
              </div>
            </div>
            {disclaimer && <p className={styles.disclaimer}>{disclaimer[language]}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulatedTransaction;
